import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { useTable } from '@tanstack/react-table'
import type { FunctionReturnType } from 'convex/server'
import { IconShieldKeyholeOutline18, IconUsersOutline18 } from 'nucleo-ui-outline-18'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AdminAvatar } from '~/components/admin/admin-avatar'
import { Button } from '~/components/ui/button'
import type { DataTableFeatures } from '~/components/ui/data-table'
import {
  DATE_FORMAT_OPTIONS,
  DataTable,
  dataTableFeatures,
  dataTableSortOptions,
  makeGlobalFilter,
} from '~/components/ui/data-table'
import { EmptyState } from '~/components/ui/empty-state'
import { PageHeader } from '~/components/ui/page-header'
import { SearchField } from '~/components/ui/search-field'
import { useDebouncedValue } from '~/hooks/use-debounced-value'
import { useGrowingPage } from '~/hooks/use-growing-page'
import { confirm } from '~/lib/confirm'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/admin/users/')({
  component: AdminUsers,
  head: () => ({ meta: [{ title: titleFor(msg`Users`) }] }),
})

const PAGE_SIZE = 200

type UserRow = FunctionReturnType<typeof api.admin.listUsers>['page'][number]

const EMPTY_ROWS: UserRow[] = []

function AdminUsers() {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const { data: me } = useQuery(convexQuery(api.users.currentUser, {}))
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data, isPending } = useQuery(convexQuery(api.admin.listUsers, { paginationOpts }))
  const { data: stats } = useQuery(convexQuery(api.admin.dashboardStats, {}))
  const [sorting, setSorting] = useState<SortingState>([{ desc: true, id: 'createdAt' }])
  const [globalFilter, setGlobalFilter] = useState('')
  // Server-side search (name full-text + exact email) catches accounts
  // beyond the loaded pages; results merge into the client-filtered table.
  const debouncedSearch = useDebouncedValue(globalFilter.trim())
  const { data: searchResults } = useQuery({
    ...convexQuery(api.admin.searchUsers, { search: debouncedSearch }),
    enabled: debouncedSearch !== '',
  })
  const [filter, setFilter] = useState<'all' | 'admin'>('all')
  const { mutateAsync: setAdminFlag, isPending: isTogglingAdmin } = useMutation({
    mutationFn: useConvexMutation(api.admin.setAdminFlag),
  })

  const handleToggleAdmin = useCallback(
    async (row: UserRow) => {
      const granting = !row.admin
      const confirmed = await confirm({
        confirmLabel: granting ? t`Grant admin` : t`Revoke admin`,
        description: granting
          ? t`"${row.name}" will get full access to the cross-workspace admin surface.`
          : t`"${row.name}" will lose access to the admin surface.`,
        destructive: !granting,
        icon: <IconShieldKeyholeOutline18 />,
        title: granting ? t`Grant Planno admin?` : t`Revoke Planno admin?`,
      })
      if (!confirmed) {
        return
      }
      try {
        await setAdminFlag({ admin: granting, userId: row._id })
        toast.success(granting ? t`Admin access granted.` : t`Admin access revoked.`)
      } catch (error) {
        console.error('Toggle admin error:', error)
        toast.error(t`Couldn't update admin access.`)
      }
    },
    [t, setAdminFlag]
  )

  const columns = useMemo<ColumnDef<DataTableFeatures, UserRow>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        cell: ({ row }) => (
          // Wrapping keeps the cell's min-content width down to the longest
          // word, so a long name plus its badges don't push the table wider
          // than a phone's viewport.
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <AdminAvatar image={row.original.image} name={row.original.name} />
            <span className="font-medium">{row.original.name}</span>
            {row.original.admin ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                <IconShieldKeyholeOutline18 className="size-3" />
                {t`Admin`}
              </span>
            ) : null}
          </div>
        ),
        header: () => t`Name`,
        id: 'name',
      },
      {
        accessorKey: 'email',
        cell: ({ row }) => row.original.email || '—',
        header: () => t`Email`,
        meta: { className: 'hidden text-muted-foreground md:table-cell' },
      },
      {
        accessorKey: 'workspaceCount',
        cell: ({ row }) => i18n.number(row.original.workspaceCount),
        header: () => t`Workspaces`,
        meta: { className: 'hidden text-muted-foreground sm:table-cell' },
        sortDescFirst: true,
      },
      {
        accessorKey: 'lastActiveAt',
        cell: ({ row }) =>
          row.original.lastActiveAt === null
            ? '—'
            : formatRelativeTime(row.original.lastActiveAt, i18n),
        header: () => t`Last active`,
        meta: { className: 'hidden text-muted-foreground lg:table-cell' },
        sortDescFirst: true,
      },
      {
        accessorKey: 'createdAt',
        cell: ({ row }) => i18n.date(row.original.createdAt, DATE_FORMAT_OPTIONS),
        header: () => t`Signed up`,
        meta: { className: 'hidden text-muted-foreground sm:table-cell' },
        sortDescFirst: true,
      },
      {
        cell: ({ row }) => {
          const isSelf = me?._id === row.original._id
          return (
            <Button
              disabled={isTogglingAdmin || (isSelf && row.original.admin)}
              onClick={(event) => {
                event.stopPropagation()
                handleToggleAdmin(row.original)
              }}
              size="sm"
              title={
                isSelf && row.original.admin ? t`You can't revoke your own admin access` : undefined
              }
              type="button"
              variant="ghost"
            >
              {row.original.admin ? <Trans>Revoke admin</Trans> : <Trans>Grant admin</Trans>}
            </Button>
          )
        },
        enableSorting: false,
        header: () => null,
        id: 'actions',
        meta: { className: 'w-0 whitespace-nowrap text-right' },
      },
    ],
    [t, i18n, me?._id, isTogglingAdmin, handleToggleAdmin]
  )

  const globalFilterFn = useMemo(
    () => makeGlobalFilter<UserRow>((row) => [row.name, row.email]),
    []
  )

  const loadedRows = data?.page ?? EMPTY_ROWS

  const rows = useMemo(() => {
    if (debouncedSearch === '' || searchResults === undefined) {
      return loadedRows
    }
    const seen = new Set(loadedRows.map((row) => row._id))
    return [...loadedRows, ...searchResults.filter((row) => !seen.has(row._id))]
  }, [loadedRows, debouncedSearch, searchResults])

  const filteredData = useMemo(
    () => (filter === 'admin' ? rows.filter((u) => u.admin) : rows),
    [rows, filter]
  )

  const table = useTable({
    ...dataTableSortOptions,
    columns,
    data: filteredData,
    features: dataTableFeatures,
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: { globalFilter, sorting },
  })

  const searching = globalFilter.trim() !== ''
  const adminCount = loadedRows.filter((u) => u.admin).length
  // While searching or filtering the count reflects visible rows; otherwise
  // the real total from `dashboardStats` (loaded pages undercount past
  // page 1).
  const headerCount =
    searching || filter === 'admin'
      ? table.getRowModel().rows.length
      : (stats?.userCount ?? loadedRows.length)

  return (
    <div className="flex h-full flex-col">
      <PageHeader count={headerCount} title={t`Users`}>
        <FilterPill active={filter === 'all'} label={t`All`} onClick={() => setFilter('all')} />
        <FilterPill
          active={filter === 'admin'}
          count={adminCount}
          label={t`Admins`}
          onClick={() => setFilter('admin')}
        />
        <SearchField onChange={setGlobalFilter} value={globalFilter} />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <DataTable
          empty={
            <EmptyState
              className="animation-duration-30 animate-fade-up"
              description={
                searching ? t`Try another search term.` : t`No accounts have signed up yet.`
              }
              icon={IconUsersOutline18}
              title={searching ? t`No users found` : t`No users yet`}
            />
          }
          isPending={isPending}
          onRowClick={(row) =>
            navigate({ params: { userId: row._id }, to: '/admin/users/$userId' })
          }
          table={table}
        />
        {data !== undefined && !data.isDone ? (
          <div className="flex justify-center py-3">
            <Button onClick={loadMore} size="sm" type="button" variant="outline">
              <Trans>Load more</Trans>
            </Button>
          </div>
        ) : null}
        <DeletionRequests />
        <AdminFlagHistory />
      </div>
    </div>
  )
}

/**
 * Accounts that asked to be deleted, newest first.
 *
 * Unconditionally visible rather than hidden behind a filter — a closure
 * request is rare, it is the one thing on this page nobody should have to go
 * looking for, and the section disappears entirely when there are none. Read
 * through `by_deletionRequestedAt`, so it costs an index probe rather than a
 * scan of every account in the product.
 *
 * The lever itself lives on the user's detail page, one click away: reopening
 * an account is a decision that deserves the context of whose account it is.
 */
const DeletionRequests = () => {
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const { data } = useQuery(convexQuery(api.admin.listDeletionRequests, {}))

  if (data === undefined || data.requests.length === 0) {
    return null
  }

  return (
    <section className="mt-8">
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        <Trans>Deletion requests</Trans>
      </h2>
      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {data.requests.map((row) => (
          <li key={row._id}>
            <button
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm hover:bg-muted/50"
              onClick={() => navigate({ params: { userId: row._id }, to: '/admin/users/$userId' })}
              type="button"
            >
              <AdminAvatar image={row.image} name={row.name ?? ''} />
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{row.name}</span>
                {row.email === null ? null : (
                  <span className="text-muted-foreground"> · {row.email}</span>
                )}
              </span>
              <span className="shrink-0 text-muted-foreground text-xs">
                {formatRelativeTime(row.deletionRequestedAt, i18n)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {/* Never let a truncated list read as a complete one. */}
      {data.truncated ? (
        <p className="pt-2 text-muted-foreground text-xs">
          <Trans>
            Showing the {data.requests.length} most recent. There are more. Something is wrong.
          </Trans>
        </p>
      ) : null}
    </section>
  )
}

/**
 * The recent staff-admin grant/revoke trail, read from the audit rows
 * `setAdminFlag` writes. Hidden while loading and when there's nothing to
 * show. A `null` actor is a CLI `bootstrapAdmin` change.
 */
const AdminFlagHistory = () => {
  const { t, i18n } = useLingui()
  const { data } = useQuery(convexQuery(api.admin.adminFlagHistory, {}))

  if (data === undefined || data.length === 0) {
    return null
  }

  return (
    <section className="mt-8">
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        <Trans>Admin access changes</Trans>
      </h2>
      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {data.map((row) => {
          const actorName = row.actor?.name ?? t`System`
          const targetName = row.target?.name ?? t`Deleted account`
          return (
            <li className="flex items-center gap-2.5 px-4 py-2.5 text-sm" key={row._id}>
              <AdminAvatar image={row.actor?.image ?? null} name={actorName} />
              <span className="min-w-0 flex-1 truncate">
                {row.granted ? (
                  <Trans>
                    <span className="font-medium">{actorName}</span> granted admin access to{' '}
                    <span className="font-medium">{targetName}</span>
                  </Trans>
                ) : (
                  <Trans>
                    <span className="font-medium">{actorName}</span> revoked admin access from{' '}
                    <span className="font-medium">{targetName}</span>
                  </Trans>
                )}
              </span>
              <span className="shrink-0 text-muted-foreground text-xs">
                {formatRelativeTime(row.createdAt, i18n)}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

const FilterPill = ({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean
  label: string
  count?: number
  onClick: () => void
}) => (
  <button
    className={
      active
        ? 'rounded-md border border-foreground bg-foreground px-3 py-1.5 font-medium text-background text-xs transition'
        : 'rounded-md border border-border bg-card px-3 py-1.5 font-medium text-muted-foreground text-xs transition hover:border-foreground/30 hover:text-foreground'
    }
    onClick={onClick}
    type="button"
  >
    {label}
    {count !== undefined && count > 0 && <span className="ml-1.5 opacity-60">{count}</span>}
  </button>
)
